// Vendor bundle entry — bakes React + ReactDOM into a same-origin
// /vendor.js so the SPA has zero third-party network requests at
// runtime.
//
// The two SPA targets (app.jsx and tweaks-panel.jsx) are independent
// IIFE bundles that consume React via the legacy global pattern
// (`const { useState, ... } = React;`, `React.useState(...)`,
// `ReactDOM.createRoot(...)`). To keep that surface unchanged, this
// entry imports the production builds of React and ReactDOM and
// re-exposes them on `window.React` / `window.ReactDOM` — exactly
// what the prior `unpkg.com/react@18.3.1/umd/react.production.min.js`
// scripts did.
//
// Critical: index.html loads /vendor.js BEFORE /tweaks-panel.js and
// /app.js so the globals exist by the time those bundles run.
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import * as ReactDOMLegacy from 'react-dom';
import { filterWorkspaces, clampHighlight } from './workspace-switcher.js';
import { isModifiedClick, buildRoute } from './spa-nav.js';
import { pickActiveHeadingId } from './scrollspy.js';
import { pickRecentFiles } from './workspace-landing.js';
import * as tabsLib from './tabs.js';
import { createPreviewState } from './version-preview.js';
import { formatHistoryRow, relativeAgo, shortSha, currentVersionId } from './history-rows.js';
import { createEditBuffer } from './edit-buffer.js';
import { formatEditedAgo, formatMetaLine } from './doc-meta.js';
import { apiReadFile, apiWriteFile, apiMove, apiMoveBatch, apiCopy, apiDelete, apiRestore, apiStat, apiVersions, apiNewFile, apiRelated, StalePreconditionError, ReservedPathError, NetworkError } from './api-mutations.js';
import { isValidDrop, composeDropTarget, expandFolderMoves, collectFilesUnder, collectFolders, isDescendantOf, basename } from './dnd.js';
import { buildMovePlan, moveConfirmCopy } from './move-plan.js';
import { singleSelect, clearSelection, toggleSelection, selectRange } from './selection.js';
import { siblingNames, nextDuplicateName, validateBasename, replacePathPrefix, joinPath, splitBasename } from './tree-mutations.js';
import { shouldFireEditShortcut } from './edit-shortcut.js';
import { loadCodeMirror } from './cm-loader.js';
import {
  groupByRelativeDate,
  formatRelativeTime,
  parentPathOf,
} from './recent-groups.js';
import { clampMenuPosition, wikilinkFor, buildContextMenuItems } from './context-menu.js';
import { buildBreadcrumbItems } from './breadcrumb.js';
import {
  groupByFolder,
  selectionBytes,
  formatBytes,
  urgentClass,
  authorClass,
  splitFolderName,
  formatRelative,
} from './trash-groups.js';
import {
  apiListDeleted,
  apiRestoreOne,
  apiRestoreMany,
} from './api-trash.js';
import {
  apiHome,
  apiPinAdd,
  apiPinRemove,
  apiPinReorder,
  apiHealthSummary,
} from './api-home.js';
import { SCHEMES, STYLES, applyScheme, applyStyle } from './reader-theme.js';
import { filterRows, sortRows, tagCounts, defaultBrowseState } from './browse-rows.js';
import { escapeRegexLiteral, groupGrepMatches, fuzzyRankFiles, nextCursor, isCommandMode, filterCommands } from './search-overlay.js';
import { apiSearch, NetworkError as SearchNetworkError } from './api-search.js';

if (typeof window !== 'undefined') {
  // React's UMD build hangs the namespace under `window.React` directly,
  // so consumers can do both `React.useState` and `const { useState } = React;`.
  window.React = React;
  // React 18 splits into `react-dom` (legacy `render`) and
  // `react-dom/client` (`createRoot`). app.jsx uses `createRoot`. The UMD
  // build merges both. Mirror that: prefer the client-API shape, then
  // shallow-merge the legacy entry so anything reaching for
  // `ReactDOM.render` / `ReactDOM.findDOMNode` still resolves.
  window.ReactDOM = { ...ReactDOMLegacy, ...ReactDOM };
  window.mindexWS = { filterWorkspaces, clampHighlight };
  window.mindexSpaNav = { isModifiedClick, buildRoute };
  window.mindexScrollspy = { pickActiveHeadingId };
  window.mindexLanding = { pickRecentFiles };
  window.mindexTabs = { ...tabsLib };
  window.mindexHistory = { createPreviewState, formatHistoryRow, relativeAgo, shortSha, currentVersionId };
  window.mindexDocMeta = { formatEditedAgo, formatMetaLine };
  window.mindexEditBuffer = { createEditBuffer };
  window.mindexApiMut = { apiReadFile, apiWriteFile, apiMove, apiMoveBatch, apiCopy, apiDelete, apiRestore, apiStat, apiVersions, apiNewFile, apiRelated, StalePreconditionError, ReservedPathError, NetworkError };
  window.mindexDnd = { isValidDrop, composeDropTarget, expandFolderMoves, collectFilesUnder, collectFolders, isDescendantOf, basename };
  window.mindexMovePlan = { buildMovePlan, moveConfirmCopy };
  window.mindexSelection = { singleSelect, clearSelection, toggleSelection, selectRange };
  window.mindexTreeMut = { siblingNames, nextDuplicateName, validateBasename, replacePathPrefix, joinPath, splitBasename };
  window.mindexEditShortcut = { shouldFireEditShortcut };
  window.mindexCmLoader = { loadCodeMirror };
  window.mindexRecentGroups = { groupByRelativeDate, formatRelativeTime, parentPathOf };
  window.mindexContextMenu = { clampMenuPosition, wikilinkFor, buildContextMenuItems };
  window.mindexBreadcrumb = { buildBreadcrumbItems };
  window.mindexTrashGroups = {
    groupByFolder, selectionBytes, formatBytes, urgentClass,
    authorClass, splitFolderName, formatRelative,
  };
  window.mindexApiTrash = { apiListDeleted, apiRestoreOne, apiRestoreMany };
  window.mindexApiHome = { apiHome, apiPinAdd, apiPinRemove, apiPinReorder, apiHealthSummary };
  window.mindexReaderTheme = { SCHEMES, STYLES, applyScheme, applyStyle };
  window.mindexBrowse = { filterRows, sortRows, tagCounts, defaultBrowseState };
  window.mindexSearch = { escapeRegexLiteral, groupGrepMatches, fuzzyRankFiles, nextCursor, isCommandMode, filterCommands };
  window.mindexApiSearch = { apiSearch, NetworkError: SearchNetworkError };
}
